// Copyright (c) StrangeBee 2023

import React, { ReactElement, useMemo } from 'react';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { THLayout } from '@Molecules/THLayout';
import { useCurrentUser } from '@Queries/userQueries';
import { computeEventList } from '@Organisms/User/eventList';
import { useLocalMessage } from '@Helpers/locale';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import client from '@Client';
import useValidateForm from '@Hooks/useValidateForm';
import { APIKeyButton } from '@Molecules/APIKeyButton/APIKeyButton';
import usePageTitle from '@Hooks/usePageTitle';

import './APIKey.scss';

const APIKey = (): ReactElement => {
    const { data: currentUser, refetch } = useCurrentUser();
    const i = useLocalMessage();
    const {} = useValidateForm();
    const { _id: userId = '', hasKey = false } = currentUser || {};

    const eventList = useMemo(
        () =>
            computeEventList({
                locale: i,
                controller: client.UserController,
                onSuccess: (_, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    refetch();
                },
                onError: handleNotificationError,
            }),
        [i, refetch],
    );

    usePageTitle(i('page-title.account.api-key'));

    return (
        <THLayout>
            <THLayout.Content>
                <div className="api-key__container">
                    <APIKeyButton
                        className="api-key"
                        hasKey={hasKey}
                        userId={userId}
                        onGet={eventList.onGetAPIKey}
                        onRenew={eventList.onCreateAPIKey}
                        onRevoke={eventList.onRevokeAPIKey}
                    />
                </div>
            </THLayout.Content>
        </THLayout>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'API Key',
    },
    path: RouteBuilder.compute('account.api'),
    content: <APIKey />,
    space: ['admin', 'user'],
    breadcrumb: { label: 'API Key' },
};
